// PHP API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://iamar.ma/php-api';

// Helper for HTTP requests
const fetchApi = async (url, options = {}) => {
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  const token = localStorage.getItem('iamar_admin_token');
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.error || `HTTP error ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`API Error on ${url}:`, error);
    throw error;
  }
};

// ============================================
// AUTHENTICATION API
// ============================================
export const authApi = {
  login: async ({ email, password }) => {
    const data = await fetchApi(`${API_BASE_URL}/auth`, {
      method: 'POST',
      body: JSON.stringify({ email, username: email, password }),
    });
    if (data && data.token) {
      localStorage.setItem('iamar_admin_token', data.token);
      localStorage.setItem('iamar_admin_user', JSON.stringify(data.user));
    }
    return data;
  },

  getCurrentUser: () => {
    try {
      const user = localStorage.getItem('iamar_admin_user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('iamar_admin_token');
  },

  logout: () => {
    localStorage.removeItem('iamar_admin_token');
    localStorage.removeItem('iamar_admin_user');
  },
};

// ============================================
// ADMIN USERS MANAGEMENT API
// ============================================
export const adminUsersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/users`),
  getById: (id) => fetchApi(`${API_BASE_URL}/users/${id}`),
  create: (user) => fetchApi(`${API_BASE_URL}/users`, { method: 'POST', body: JSON.stringify(user) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/users/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/users/${id}`, { method: 'DELETE' }),
};

// ============================================
// GSIDA SPEAKERS API
// ============================================
export const gsidaSpeakersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/speakers`),
  getById: (id) => fetchApi(`${API_BASE_URL}/speakers/${id}`),
  create: (speaker) => fetchApi(`${API_BASE_URL}/speakers`, { method: 'POST', body: JSON.stringify(speaker) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/speakers/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/speakers/${id}`, { method: 'DELETE' }),
};

// ============================================
// GSIDA PUBLICATIONS API
// ============================================
export const gsidaPublicationsApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/publications`),
  getById: (id) => fetchApi(`${API_BASE_URL}/publications/${id}`),
  create: (pub) => fetchApi(`${API_BASE_URL}/publications`, { method: 'POST', body: JSON.stringify(pub) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/publications/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/publications/${id}`, { method: 'DELETE' }),
};

// ============================================
// GSIDA GALLERY API
// ============================================
export const gsidaGalleryApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/gallery`),
  getById: (id) => fetchApi(`${API_BASE_URL}/gallery/${id}`),
  create: (media) => fetchApi(`${API_BASE_URL}/gallery`, { method: 'POST', body: JSON.stringify(media) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/gallery/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/gallery/${id}`, { method: 'DELETE' }),
};

// ============================================
// GSIDA PARTNERS API
// ============================================
export const gsidaPartnersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/partners`),
  getById: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`),
  create: (partner) => fetchApi(`${API_BASE_URL}/partners`, { method: 'POST', body: JSON.stringify(partner) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'DELETE' }),
};

// ============================================
// APSIS EXHIBITORS API
// ============================================
export const apsisExhibitorsApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/exhibitors`),
  getById: (id) => fetchApi(`${API_BASE_URL}/exhibitors/${id}`),
  create: (item) => fetchApi(`${API_BASE_URL}/exhibitors`, { method: 'POST', body: JSON.stringify(item) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/exhibitors/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/exhibitors/${id}`, { method: 'DELETE' }),
};

// ============================================
// APSIS INDUSTRIAL PARTNERS API
// ============================================
export const apsisIndustrialPartnersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/partners`),
  getById: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`),
  create: (item) => fetchApi(`${API_BASE_URL}/partners`, { method: 'POST', body: JSON.stringify(item) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'DELETE' }),
};

// ============================================
// GOVERNANCE MEMBERS API
// ============================================
export const governanceMembersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/governance`),
  getById: (id) => fetchApi(`${API_BASE_URL}/governance/${id}`),
  create: (item) => fetchApi(`${API_BASE_URL}/governance`, { method: 'POST', body: JSON.stringify(item) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/governance/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/governance/${id}`, { method: 'DELETE' }),
};

// Image URL Resolver Helper
export const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
    return url;
  }
  const baseUrl = (process.env.REACT_APP_API_URL || 'https://iamar.ma/php-api').replace(/\/php-api\/?$/, '');
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanPath}`;
};

const DEFAULT_HERO_SLIDES = [
  {
    id: 1,
    image_url: "https://blogger.googleusercontent.com/img/a/AVvXsEj_ACBhNpK2Jw2eNZe-pi2ymltzruB1dvHxr1j6hfSVVBBYX50mcIiuUMM9KC3tW7FH2Wt7SoHu7GIKN_QJACzSev_n5Q91B_kKZxiMypi-9iSTVd1xIIf0GBop_MXxBZRIyOo0TdNIGeVtDBYE9oTwziDZB0pSyvLthBCzwA1HMLC3WSjVNqBU0mZnG_hO=s16000",
    caption: "GSIDA – Gouvernance, Innovation, Développement en Afrique",
    title: "GSIDA 2026",
    subtitle: "Gouvernance & Innovation",
    order_position: 1,
    is_active: true
  },
  {
    id: 2,
    image_url: "https://www.iris-france.org/wp-content/uploads/2024/01/Fotolia_3342960_XL-1.jpg-1-1024x724.jpg",
    caption: "APSIS – Agadir Private Security & Innovation Summit",
    title: "APSIS 2026",
    subtitle: "Sommet Sécurité Privée",
    order_position: 2,
    is_active: true
  },
  {
    id: 3,
    image_url: "https://cdn.prod.website-files.com/65746cc6772cc7936b34bc87/668e4d293c9ac632733b5498_bsb-crd-3.webp",
    caption: "GSIDA Academic Challenge – L'excellence en action",
    title: "Academic Challenge",
    subtitle: "Excellence académique",
    order_position: 3,
    is_active: true
  },
  {
    id: 4,
    image_url: "https://www.groupeiscae.ma/wp-content/uploads/2022/10/Hiba-amaize-1-scaled.jpg",
    caption: "GSIDA 2026 – Gouvernance, innovation et durabilité",
    title: "Leadership & Impact",
    subtitle: "Développement durable",
    order_position: 4,
    is_active: true
  }
];

// ============================================
// HERO SLIDES API (with local persistent cache fallback)
// ============================================
export const heroSlidesApi = {
  getAll: async () => {
    try {
      const data = await fetchApi(`${API_BASE_URL}/hero-slides`);
      if (Array.isArray(data) && data.length > 0) {
        localStorage.setItem('iamar_hero_slides', JSON.stringify(data));
        return data;
      }
    } catch (err) {
      console.warn("Could not fetch hero slides from API, falling back to local storage:", err.message);
    }

    try {
      const saved = localStorage.getItem('iamar_hero_slides');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.error(e);
    }

    return DEFAULT_HERO_SLIDES;
  },

  getById: (id) => fetchApi(`${API_BASE_URL}/hero-slides/${id}`),

  create: async (slide) => {
    const newSlide = {
      ...slide,
      id: slide.id || Date.now(),
      created_at: new Date().toISOString()
    };

    // Update local cache immediately
    try {
      const current = await heroSlidesApi.getAll();
      const updated = [...current, newSlide];
      localStorage.setItem('iamar_hero_slides', JSON.stringify(updated));
    } catch (e) {}

    // Try server update
    try {
      return await fetchApi(`${API_BASE_URL}/hero-slides`, { method: 'POST', body: JSON.stringify(slide) });
    } catch (err) {
      console.warn("Server save failed, saved locally:", err);
      return newSlide;
    }
  },

  update: async (id, updates) => {
    // Update local cache immediately
    try {
      const current = await heroSlidesApi.getAll();
      const updated = current.map(s => String(s.id) === String(id) ? { ...s, ...updates } : s);
      localStorage.setItem('iamar_hero_slides', JSON.stringify(updated));
    } catch (e) {}

    // Try server update
    try {
      return await fetchApi(`${API_BASE_URL}/hero-slides/${id}`, { method: 'PUT', body: JSON.stringify(updates) });
    } catch (err) {
      console.warn("Server update failed, updated locally:", err);
      return { id, ...updates };
    }
  },

  delete: async (id) => {
    // Update local cache immediately
    try {
      const current = await heroSlidesApi.getAll();
      const updated = current.filter(s => String(s.id) !== String(id));
      localStorage.setItem('iamar_hero_slides', JSON.stringify(updated));
    } catch (e) {}

    // Try server delete
    try {
      return await fetchApi(`${API_BASE_URL}/hero-slides/${id}`, { method: 'DELETE' });
    } catch (err) {
      console.warn("Server delete failed, removed locally:", err);
      return { success: true };
    }
  },
};

// ============================================
// ABOUT SECTIONS API
// ============================================
export const aboutSectionsApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/about-sections`),
  getById: (id) => fetchApi(`${API_BASE_URL}/about-sections/${id}`),
  create: (section) => fetchApi(`${API_BASE_URL}/about-sections`, { method: 'POST', body: JSON.stringify(section) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/about-sections/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/about-sections/${id}`, { method: 'DELETE' }),
};

// ============================================
// PARTNERS API (General)
// ============================================
export const partnersApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/partners`),
  getById: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`),
  create: (partner) => fetchApi(`${API_BASE_URL}/partners`, { method: 'POST', body: JSON.stringify(partner) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/partners/${id}`, { method: 'DELETE' }),
};

// ============================================
// NEWS API
// ============================================
export const newsApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/news`),
  getById: (id) => fetchApi(`${API_BASE_URL}/news/${id}`),
  create: (newsItem) => fetchApi(`${API_BASE_URL}/news`, { method: 'POST', body: JSON.stringify(newsItem) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/news/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/news/${id}`, { method: 'DELETE' }),
};

// ============================================
// TESTIMONIALS API
// ============================================
export const testimonialsApi = {
  get: () => fetchApi(`${API_BASE_URL}/testimonials`),
  getAll: () => fetchApi(`${API_BASE_URL}/testimonials`),
  create: (testimonial) => fetchApi(`${API_BASE_URL}/testimonials`, { method: 'POST', body: JSON.stringify(testimonial) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
};

// ============================================
// CONTACT INFO API
// ============================================
export const contactInfoApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/contact-info`),
  getById: (id) => fetchApi(`${API_BASE_URL}/contact-info/${id}`),
  create: (contact) => fetchApi(`${API_BASE_URL}/contact-info`, { method: 'POST', body: JSON.stringify(contact) }),
  update: (id, updates) => fetchApi(`${API_BASE_URL}/contact-info/${id}`, { method: 'PUT', body: JSON.stringify(updates) }),
  delete: (id) => fetchApi(`${API_BASE_URL}/contact-info/${id}`, { method: 'DELETE' }),
};

// ============================================
// SITE CONTENT & PAGE TITLES API
// ============================================
export const siteContentApi = {
  getAll: () => fetchApi(`${API_BASE_URL}/site-content`),
  getByKey: (key) => fetchApi(`${API_BASE_URL}/site-content/${key}`),
  save: (content) => fetchApi(`${API_BASE_URL}/site-content`, { method: 'POST', body: JSON.stringify(content) }),
  delete: (key) => fetchApi(`${API_BASE_URL}/site-content/${key}`, { method: 'DELETE' }),
};

// ============================================
// FILE UPLOAD API
// ============================================
export const fileUploadApi = {
  upload: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('iamar_admin_token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.warn("Upload to server failed or offline, using Data URL fallback:", err);
      // Fallback: convert file to Base64 Data URL so admin can preview & save without error
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ url: reader.result, filename: file.name, isLocal: true });
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    }
  },
  uploadImage: async (file) => {
    const result = await fileUploadApi.upload(file);
    return result.url;
  },
  uploadPdf: async (file) => {
    const result = await fileUploadApi.upload(file);
    return result.url;
  },
  uploadFile: async (file) => {
    const result = await fileUploadApi.upload(file);
    return result.url;
  },
};